/**
 * Stack — React (web) implementation.
 *
 * A layout container that arranges children along a single axis.
 * All spacing values reference the design system's spacing tokens.
 * No raw hex values or pixel sizes in this file.
 *
 * Accessibility: Stack is transparent — no ARIA attributes.
 * Children retain their own semantics.
 */

import React, { type ReactNode } from 'react';
import type { StackProps } from './Stack';
import {
  spacingValue,
  alignToFlex,
  justifyToFlex,
  stackBackgroundColorFor,
  stackInsetPadding,
  STACK_PILL_BACKGROUND,
  STACK_PILL_PADDING_LEADING,
  STACK_PILL_PADDING_TRAILING,
  STACK_PILL_PADDING_VERTICAL,
  STACK_PILL_RADIUS,
} from './Stack';
import { SCREEN_GUTTER, SCREEN_GUTTER_KEY } from '../../elements/spacing';

export interface WebStackProps extends Partial<StackProps> {
  children?: ReactNode;
  'data-testid'?: string;
}

export function Stack({
  direction = 'vertical',
  gap = 'none',
  padding = 'none',
  paddingVertical,
  paddingHorizontal,
  align = 'stretch',
  justify = 'start',
  wrap = false,
  flex,
  width,
  background,
  bleed = false,
  shape,
  inset,
  children,
  'data-testid': testId,
}: WebStackProps) {
  const insetPadding = inset === undefined ? undefined : stackInsetPadding(inset);
  const style: React.CSSProperties = {
    display: 'flex',
    flexDirection: direction === 'vertical' ? 'column' : 'row',
    gap: spacingValue(gap),
    paddingBlock: spacingValue(paddingVertical ?? padding),
    // A bleed re-insets by the gutter it cancelled, so `bleed` alone is
    // correct; an explicit paddingHorizontal still wins.
    paddingInline: spacingValue(paddingHorizontal ?? (bleed ? SCREEN_GUTTER_KEY : padding)),
    alignItems: alignToFlex(align),
    justifyContent: justifyToFlex(justify),
    flexWrap: wrap ? 'wrap' : 'nowrap',
    ...(flex !== undefined ? { flex } : {}),
    // `border-box` so a fixed width means the same thing here as it does on
    // native, where Yoga is always border-box. Without it CSS `content-box`
    // puts the padding OUTSIDE the width, so a padded fixed-width Stack renders
    // wider than it asks for and its `Full`-width children take the whole
    // declared width instead of the width net of padding. `Label.web.tsx` pairs
    // the two properties for the same reason.
    ...(width !== undefined ? { width, boxSizing: 'border-box', flexGrow: 0, flexShrink: 0 } : {}),
    ...(background !== undefined ? { backgroundColor: stackBackgroundColorFor[background] } : {}),
    ...(bleed ? { marginInline: -SCREEN_GUTTER } : {}),
    // Longhands after the `paddingBlock` / `paddingInline` shorthands above, so
    // they win on the axis they set.
    ...(shape === 'pill'
      ? {
          backgroundColor: STACK_PILL_BACKGROUND,
          borderRadius: STACK_PILL_RADIUS,
          paddingBlock: STACK_PILL_PADDING_VERTICAL,
          paddingInlineStart: STACK_PILL_PADDING_LEADING,
          paddingInlineEnd: STACK_PILL_PADDING_TRAILING,
        }
      : {}),
    ...(insetPadding
      ? { paddingInlineStart: insetPadding.leading, paddingInlineEnd: insetPadding.trailing }
      : {}),
  };

  return (
    <div style={style} data-testid={testId}>
      {children}
    </div>
  );
}
